/* istanbul ignore file */
import React from 'react';
import usePurchaseOrderService, {
  GOODS_AND_SERVICES_DELIVERY_STATUS_OPTIONS,
  PURCHASE_ORDER_STATUSES,
} from 'domain/finance/purchaseOrder/usePurchaseOrderService';
import { Box } from '@mui/material';
import InvoiceLink from 'domain/finance/invoice/shared/field/InvoiceLink';
import ViewInvoiceButton from 'domain/finance/invoice/shared/button/ViewInvoiceButton';
import { EnumField, TextField, BadgeField, DateField } from 'framework/ui/field';
import { InvoiceTotalField } from 'domain/shared/core/invoice';
import MutableInvoiceBadge from 'domain/finance/invoice/shared/MutableInvoiceBadge';
import MarkAsDeliveredGoodsAndServicesButton from 'domain/finance/invoice/shared/button/MarkAsDeliveredGoodsAndServicesButton';
import { useShowContext } from 'framework/show';
import TempInvoiceVendorNameField from 'domain/finance/invoice/shared/field/TempInvoiceVendorNameField';
import { DataGridLayout } from 'framework/list';

const InvoicesListDataGrid = ({
  isSensitiveDataIsLoading,
  isSensitiveDataIsShown,
  isSensitivePurchaseDataIsShown,
  reloadSensitiveData,
}: {
  isSensitiveDataIsLoading?: boolean;
  isSensitiveDataIsShown?: boolean;
  isSensitivePurchaseDataIsShown?: boolean;
  reloadSensitiveData?: () => void;
}) => {
  const { getById } = usePurchaseOrderService();
  const { data, setData = () => {} } = useShowContext();

  return (
    <Box sx={{ minHeight: '200px', position: 'relative', '& .th-buttons:last-child': { width: '170px' } }}>
      <DataGridLayout
        rowKeySource="invoiceId"
        buttons={
          [
            <ViewInvoiceButton source="invoice.id" />,
            data.getValueBySource('status') === PURCHASE_ORDER_STATUSES.APPROVED ||
            data.getValueBySource('status') === PURCHASE_ORDER_STATUSES.PARTIALLY_CLOSED ? (
              <MarkAsDeliveredGoodsAndServicesButton
                deliveryStatusSource="invoice.goodsAndServices.deliveryStatus"
                invoiceIdSource="invoice.id"
                futureExpenseSource="invoice.isFutureExpense"
                invoiceStatusSource="invoice.status"
                afterSubmit={{
                  execute: async (response: any) => {
                    if (isSensitiveDataIsShown && reloadSensitiveData) {
                      await reloadSensitiveData();
                    }
                    if (response.linkedPurchaseOrder.id) {
                      const res = await getById(response.linkedPurchaseOrder.id, true);
                      setData(res);
                    }
                  },
                }}
              />
            ) : null,
          ].filter(Boolean) as React.ReactElement[]
        }
      >
        {
          [
            <BadgeField source="invoice.id" label="Invoice number" badges={[<MutableInvoiceBadge />]} width="15%">
              <InvoiceLink source="invoice.id" nameSource="invoice.invoiceNumber" label="Invoice number" />
            </BadgeField>,
            <TempInvoiceVendorNameField
              nameSource="invoice.name"
              vendorSource="invoice.vendorId"
              vendorNameSource="invoice.name"
              label="Vendor name"
              width="25%"
            />,
            isSensitivePurchaseDataIsShown && isSensitiveDataIsShown && !isSensitiveDataIsLoading ? (
              <InvoiceTotalField
                label="Total"
                source="subTotal.amount"
                currencySource="subTotal.currency"
                width="15%"
              />
            ) : null,
            isSensitivePurchaseDataIsShown && isSensitiveDataIsShown && !isSensitiveDataIsLoading ? (
              <InvoiceTotalField
                label="Delivered amount"
                source="invoiceDeliveredSubTotal.amount"
                currencySource="invoiceDeliveredSubTotal.currency"
                width="25%"
              />
            ) : null,
            <EnumField
              label="Status"
              source="invoice.goodsAndServices.deliveryStatus"
              options={GOODS_AND_SERVICES_DELIVERY_STATUS_OPTIONS}
              width="15%"
            />,
            <DateField source="invoice.goodsAndServices.deliveryDate" label="Delivery date" width="20%" />,
            <TextField source="invoice.description" width="27%" />,
          ].filter(Boolean) as React.ReactElement[]
        }
      </DataGridLayout>
    </Box>
  ) as any;
};

export default InvoicesListDataGrid;
